import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../context/UserContext';
import { getAllStudents } from '../services/userService';
import {
  getScenarios, addScenario, deleteScenario,
  getAssignedScenarioIds, assignScenarioToStudent, unassignScenarioFromStudent,
} from '../services/dailyLivingService';
import Toast from './Toast';
import './AdminDailyLivingPage.css';

const CATEGORIES = ['hygiene', 'money', 'cooking_safety', 'street_safety', 'asking_for_help'];
const CATEGORY_LABELS = {
  hygiene: 'Hygiene',
  money: 'Money',
  cooking_safety: 'Cooking Safety',
  street_safety: 'Street Safety',
  asking_for_help: 'Asking for Help',
};

const TAB_SCENARIOS = 'scenarios';
const TAB_ASSIGN    = 'assign';

const emptyStep = () => ({ order: 1, instruction: '', image: '', options: ['', ''], correct_option: 0, voice_prompt: true });

// ─── New scenario form (step-by-step add/remove UI - no raw JSON, DLADM-01/04) ──
const NewScenarioForm = ({ currentUserId, onCreated, onError }) => {
  const [title, setTitle]           = useState('');
  const [category, setCategory]     = useState(CATEGORIES[0]);
  const [difficulty, setDifficulty] = useState(1);
  const [steps, setSteps]           = useState([emptyStep()]);
  const [saving, setSaving]         = useState(false);

  const updateStep = (index, patch) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const updateOption = (stepIndex, optIndex, value) => {
    setSteps((prev) => prev.map((s, i) => {
      if (i !== stepIndex) return s;
      const options = [...s.options];
      options[optIndex] = value;
      return { ...s, options };
    }));
  };

  const addOption = (stepIndex) => {
    setSteps((prev) => prev.map((s, i) => (i === stepIndex && s.options.length < 3 ? { ...s, options: [...s.options, ''] } : s)));
  };

  const removeOption = (stepIndex, optIndex) => {
    setSteps((prev) => prev.map((s, i) => {
      if (i !== stepIndex || s.options.length <= 2) return s;
      const options = s.options.filter((_, oi) => oi !== optIndex);
      const correct_option = s.correct_option >= options.length ? 0 : s.correct_option;
      return { ...s, options, correct_option };
    }));
  };

  const addStep = () => setSteps((prev) => [...prev, { ...emptyStep(), order: prev.length + 1 }]);

  const removeStep = (index) => {
    setSteps((prev) => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 })));
  };

  // Reject 0-step or malformed scenarios before submission (DLADM-04)
  const validate = () => {
    if (!title.trim()) return 'Title is required.';
    if (steps.length === 0) return 'A scenario needs at least one step.';
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i];
      if (!s.instruction.trim()) return `Step ${i + 1}: instruction is required.`;
      if (!s.image.trim()) return `Step ${i + 1}: icon class is required (e.g. fa-faucet).`;
      if (s.options.length < 2 || s.options.length > 3) return `Step ${i + 1}: must have 2-3 options.`;
      if (s.options.some((o) => !o.trim())) return `Step ${i + 1}: all options must have text.`;
      if (s.correct_option < 0 || s.correct_option >= s.options.length) return `Step ${i + 1}: pick a valid correct answer.`;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { onError(validationError); return; }

    setSaving(true);
    const cleanSteps = steps.map((s, i) => ({ ...s, order: i + 1, voice_prompt: true }));
    const { error } = await addScenario({ title, category, difficulty, steps: cleanSteps, createdBy: currentUserId });
    setSaving(false);
    if (error) { onError(error.message || 'Failed to create scenario.'); return; }

    setTitle('');
    setCategory(CATEGORIES[0]);
    setDifficulty(1);
    setSteps([emptyStep()]);
    onCreated();
  };

  return (
    <form onSubmit={handleSubmit} className="dla-form">
      <div className="dla-form-row">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Scenario title (e.g. Washing Hands)" className="dla-input" />
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="dla-input">
          {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
        </select>
        <select value={difficulty} onChange={(e) => setDifficulty(parseInt(e.target.value, 10))} className="dla-input dla-select-narrow">
          <option value={1}>Difficulty 1</option>
          <option value={2}>Difficulty 2</option>
          <option value={3}>Difficulty 3</option>
        </select>
      </div>

      {steps.map((s, i) => (
        <div key={i} className="dla-step-card">
          <div className="dla-step-card-header">
            <strong>Step {i + 1}</strong>
            {steps.length > 1 && (
              <button type="button" className="dla-remove-btn" onClick={() => removeStep(i)}>Remove Step</button>
            )}
          </div>
          <input
            value={s.instruction}
            onChange={(e) => updateStep(i, { instruction: e.target.value })}
            placeholder="Instruction (e.g. Turn on the water)"
            className="dla-input"
          />
          <input
            value={s.image}
            onChange={(e) => updateStep(i, { image: e.target.value })}
            placeholder="Icon class (e.g. fa-faucet)"
            className="dla-input"
          />
          {s.options.map((opt, oi) => (
            <div key={oi} className="dla-option-row">
              <input
                type="radio"
                name={`correct-${i}`}
                checked={s.correct_option === oi}
                onChange={() => updateStep(i, { correct_option: oi })}
                aria-label={`Mark option ${oi + 1} correct`}
              />
              <input
                value={opt}
                onChange={(e) => updateOption(i, oi, e.target.value)}
                placeholder={`Option ${oi + 1}`}
                className="dla-input"
              />
              {s.options.length > 2 && (
                <button type="button" className="dla-remove-btn" onClick={() => removeOption(i, oi)}>✕</button>
              )}
            </div>
          ))}
          {s.options.length < 3 && (
            <button type="button" className="dla-add-btn" onClick={() => addOption(i)}>+ Add Option</button>
          )}
        </div>
      ))}

      <div className="dla-form-actions">
        <button type="button" className="dla-add-btn" onClick={addStep}>+ Add Step</button>
        <button type="submit" className="action-btn" disabled={saving}>{saving ? 'Creating...' : 'Create Scenario'}</button>
      </div>
    </form>
  );
};

// ─── Assign scenarios to students (mirrors Math Concepts pattern, DLADM-02) ──
const AssignScenariosTab = ({ scenarios, currentUserId }) => {
  const [students, setStudents]             = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [assignedIds, setAssignedIds]       = useState(new Set());
  const [loading, setLoading]               = useState(false);
  const [toggling, setToggling]             = useState(null);

  useEffect(() => { getAllStudents().then(({ data }) => setStudents(data || [])); }, []);

  const loadAssigned = useCallback(async () => {
    if (!selectedStudent) return;
    setLoading(true);
    const { data } = await getAssignedScenarioIds(selectedStudent);
    setAssignedIds(new Set(data || []));
    setLoading(false);
  }, [selectedStudent]);

  useEffect(() => { loadAssigned(); }, [loadAssigned]);

  const handleToggle = async (scenarioId, currentlyAssigned) => {
    if (!selectedStudent || toggling) return;
    setToggling(scenarioId);

    setAssignedIds((prev) => {
      const next = new Set(prev);
      currentlyAssigned ? next.delete(scenarioId) : next.add(scenarioId);
      return next;
    });

    const fn = currentlyAssigned ? unassignScenarioFromStudent : assignScenarioToStudent;
    const params = currentlyAssigned
      ? { studentId: selectedStudent, scenarioId }
      : { studentId: selectedStudent, scenarioId, assignedBy: currentUserId };
    await fn(params);
    setToggling(null);
  };

  const selectedStudentProfile = students.find((s) => s.id === selectedStudent);

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <label htmlFor="dl-student-select" style={{ marginRight: '0.5rem', fontWeight: 'bold' }}>
          Select Student:
        </label>
        <select
          id="dl-student-select"
          value={selectedStudent}
          onChange={(e) => setSelectedStudent(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '220px' }}
        >
          <option value="">-- Choose a student --</option>
          {students.map((s) => (
            <option key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.username})</option>
          ))}
        </select>
      </div>

      {!selectedStudent ? (
        <p style={{ color: '#888' }}>Select a student above to manage their Daily Living scenario assignments.</p>
      ) : (
        <>
          <p style={{ marginBottom: '1rem' }}>
            Managing scenarios for: <strong>{selectedStudentProfile?.first_name} {selectedStudentProfile?.last_name}</strong>
            &nbsp;({assignedIds.size} assigned)
          </p>
          {loading ? <p>Loading...</p> : scenarios.length === 0 ? (
            <p>No scenarios created yet. Add one in the Scenarios tab first.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
              {scenarios.map((s) => {
                const assigned = assignedIds.has(s.id);
                return (
                  <div
                    key={s.id}
                    onClick={() => handleToggle(s.id, assigned)}
                    className={`dla-assign-card ${assigned ? 'assigned' : ''}`}
                  >
                    <i className={s.steps[0]?.image || 'fa-star'} aria-hidden="true"></i>
                    <span>{s.title}</span>
                    {assigned && <i className="fas fa-check-circle dla-assigned-check" aria-hidden="true"></i>}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────
const AdminDailyLivingPage = () => {
  const { currentUser } = useUser();
  const [scenarios, setScenarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(TAB_SCENARIOS);
  const [toast, setToast]         = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const loadScenarios = useCallback(() => {
    setIsLoading(true);
    getScenarios().then(({ data }) => {
      setScenarios(data || []);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  const handleCreated = () => {
    setToast({ message: 'Scenario created!', type: 'success' });
    loadScenarios();
  };

  const handleError = (msg) => setToast({ message: msg, type: 'error' });

  const confirmDelete = async () => {
    const id = pendingDelete;
    setPendingDelete(null);
    const { error } = await deleteScenario(id);
    if (error) { setToast({ message: error.message, type: 'error' }); return; }
    setToast({ message: 'Scenario deleted.', type: 'success' });
    loadScenarios();
  };

  return (
    <div className="content-management">
      <h1>Daily Living Skills</h1>
      <p style={{ color: '#666', marginBottom: '1rem' }}>
        Manage step-by-step life-skill scenarios (hygiene, safety, etc.) and assign them to students.
      </p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem' }}>
        <button
          onClick={() => setActiveTab(TAB_SCENARIOS)}
          className={`dla-tab-btn ${activeTab === TAB_SCENARIOS ? 'active' : ''}`}
        >
          Scenarios
        </button>
        <button
          onClick={() => setActiveTab(TAB_ASSIGN)}
          className={`dla-tab-btn ${activeTab === TAB_ASSIGN ? 'active' : ''}`}
        >
          Assign to Students
        </button>
      </div>

      {activeTab === TAB_SCENARIOS && (
        <>
          <h3>Create New Scenario</h3>
          <NewScenarioForm currentUserId={currentUser?.id} onCreated={handleCreated} onError={handleError} />

          <h3 style={{ marginTop: '2rem' }}>Existing Scenarios</h3>
          {isLoading ? (
            <p>Loading...</p>
          ) : scenarios.length === 0 ? (
            <p>No scenarios yet. Create one above.</p>
          ) : (
            <div className="dla-scenario-admin-list">
              {scenarios.map((s) => (
                <div key={s.id} className="dla-scenario-admin-card">
                  <div className="dla-scenario-admin-info">
                    <strong>{s.title}</strong>
                    <span className="dla-scenario-admin-meta">
                      {CATEGORY_LABELS[s.category] || s.category} · Difficulty {s.difficulty} · {s.steps.length} steps
                    </span>
                  </div>
                  <button className="dla-remove-btn" onClick={() => setPendingDelete(s.id)}>Delete</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === TAB_ASSIGN && (
        <AssignScenariosTab scenarios={scenarios} currentUserId={currentUser?.id} />
      )}

      {pendingDelete && (
        <div className="dla-confirm-overlay" onClick={() => setPendingDelete(null)}>
          <div className="dla-confirm-box" onClick={(e) => e.stopPropagation()}>
            <p>Delete this scenario? Students will lose access, but past completions stay on record.</p>
            <div className="dla-form-actions">
              <button className="dla-add-btn" onClick={() => setPendingDelete(null)}>Cancel</button>
              <button className="action-btn" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </div>
  );
};

export default AdminDailyLivingPage;
